import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type { InvitationData, TemplateData } from '@/types/invitation'

export const BUILDER_STORAGE_KEY = 'dday-builder-storage'

interface BuilderStoreState {
  // Root state
  invitationData: InvitationData

  // Save state tracking
  isSaved: boolean

  // Actions
  setInvitationData: (data: Partial<InvitationData>) => void
  updateInvitationData: (data: Partial<InvitationData>) => void
  updateTemplateData: (data: Partial<TemplateData>) => void
  updateBuilderOption: (optionId: string, data: Record<string, unknown>) => void
  // Init variants: merge data without marking as unsaved (for component default initialization)
  initInvitationData: (data: Partial<InvitationData>) => void
  initBuilderOption: (optionId: string, data: Record<string, unknown>) => void
  clearBuilder: () => void

  // Invitation ID action
  initializeInvitationId: () => string // Generate new UUID if not exists

  // Save state actions
  markAsSaved: () => void
  markAsUnsaved: () => void

  // URL replacement (for image transfer)
  replaceImageUrls: (urlMap: Record<string, string>) => void

  // Getters
  getInvitationData: () => InvitationData
  hasUnsavedChanges: () => boolean
}

const initialInvitationData: InvitationData = {
  // Invitation-level fields
  title: '',
  eventDateTime: null,
  eventTimezone: null,
  rsvpDeadline: null,
  enableRsvpApproval: false,
  enableGuestbookApproval: false,
  enableMaybe: false,
  enableLockMode: false,
  lockModePasscode: null,
  status: 'draft',
  // Guest upload settings
  guestUpload: {
    enabled: true,
    enablePublic: false,
  },
  // Notification settings
  // All notifications default to false - user must opt-in after logging in
  notificationSettings: {
    emailEnabled: false,
    smsEnabled: false,
    dailyRsvpReport: false,
    onUpcomingEvent: false,
    guestMaybeResponse: false,
    guestUpcomingEvent: false,
  },
  // Template-specific data
  templateData: {
    intro: {
      enabled: false,
    },
    location: {
      eventType: 'in-person',
      venueName: '',
      address: '',
      enableMap: true,
      enableDirectionsButton: true,
    },
    rsvp: {
      requiredInfo: ['name', 'phone'],
      enableRsvpApproval: false,
      enableGuestOf: true,
      guestOf: ['Bride', 'Groom'],
    },
    addToCalendar: {
      enabled: false,
    },
    guestBook: {
      enabled: false,
      requireApproval: false,
      allowAnonymous: false,
      coverColor: null,
    },
    guestUpload: {
      enabled: true,
      enablePublic: false,
    },
    gift: {
      enabled: false,
      venmoHandle: null,
      zelleHandle: null,
      cashAppHandle: null,
    },
    middlePicture: {
      enabled: false,
      selectedDesign: null,
      imageUrl: undefined,
    },
    bottomPicture: {
      enabled: false,
      imageUrl: undefined,
    },
  },
}

export const useBuilderStore = create<BuilderStoreState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        invitationData: initialInvitationData,
        isSaved: false,

        // Set invitation data (for initialization)
        setInvitationData: (data) =>
          set(
            (prev) => ({
              invitationData: { ...prev.invitationData, ...data },
              isSaved: true,
            }),
            false,
            'builder/setInvitationData'
          ),

        // Update top-level fields (title, status, etc.)
        updateInvitationData: (data) =>
          set(
            (prev) => ({
              invitationData: { ...prev.invitationData, ...data },
              isSaved: false,
            }),
            false,
            'builder/updateInvitationData'
          ),

        // Update templateData (bulk)
        updateTemplateData: (data) =>
          set(
            (prev) => ({
              invitationData: {
                ...prev.invitationData,
                templateData: { ...prev.invitationData.templateData, ...data },
              },
              isSaved: false,
            }),
            false,
            'builder/updateTemplateData'
          ),

        // Update single builder option (most common)
        updateBuilderOption: (optionId, data) =>
          set(
            (prev) => ({
              invitationData: {
                ...prev.invitationData,
                templateData: {
                  ...prev.invitationData.templateData,
                  [optionId]: data,
                },
              },
              isSaved: false,
            }),
            false,
            'builder/updateBuilderOption'
          ),

        // Init top-level fields without marking as unsaved (for component default initialization)
        initInvitationData: (data) =>
          set(
            (prev) => ({
              invitationData: { ...prev.invitationData, ...data },
            }),
            false,
            'builder/initInvitationData'
          ),

        // Init single builder option without marking as unsaved (for component default initialization)
        initBuilderOption: (optionId, data) =>
          set(
            (prev) => ({
              invitationData: {
                ...prev.invitationData,
                templateData: {
                  ...prev.invitationData.templateData,
                  [optionId]: data,
                },
              },
            }),
            false,
            'builder/initBuilderOption'
          ),

        // Clear builder and localStorage
        clearBuilder: () => {
          set(
            { invitationData: initialInvitationData, isSaved: false },
            false,
            'builder/clearBuilder'
          )
          setTimeout(() => localStorage.removeItem(BUILDER_STORAGE_KEY), 0)
        },

        markAsSaved: () => set({ isSaved: true }, false, 'builder/markAsSaved'),
        markAsUnsaved: () =>
          set({ isSaved: false }, false, 'builder/markAsUnsaved'),

        // Generate UUID for new invitations if not exists
        initializeInvitationId: () => {
          const current = get().invitationData.id
          if (current) return current
          const newId = crypto.randomUUID()
          set(
            (prev) => ({
              invitationData: { ...prev.invitationData, id: newId },
            }),
            false,
            'builder/initializeInvitationId'
          )
          return newId
        },

        // Replace image URLs in templateData (used after guest-to-user transfer)
        replaceImageUrls: (urlMap: Record<string, string>) => {
          if (Object.keys(urlMap).length === 0) return

          // Serialize current state, replace URLs, deserialize back
          const currentData = get().invitationData
          let serialized = JSON.stringify(currentData)

          for (const [oldUrl, newUrl] of Object.entries(urlMap)) {
            serialized = serialized.replaceAll(oldUrl, newUrl)
          }

          const updatedData = JSON.parse(serialized) as InvitationData

          set(
            { invitationData: updatedData },
            false,
            'builder/replaceImageUrls'
          )
        },

        getInvitationData: () => get().invitationData,
        hasUnsavedChanges: () => !get().isSaved,
      }),
      {
        name: BUILDER_STORAGE_KEY,
        // Persist invitationData and isSaved
        partialize: (state) => ({
          invitationData: state.invitationData,
          isSaved: state.isSaved,
        }),
      }
    ),
    { name: 'DDay-BuilderStore' }
  )
)
